import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useJobs } from "../../../../hooks/useJobs";
import type { StaffingViewAvailability } from "../../viewAvailability";

/**
 * Single source of truth for whether the Monthly view is offered in this department: the department is
 * non-medical and has enabled jobs (the view is job-centric, so it is meaningless with none). Both the
 * view-type selector and the route guard consume this so the Month option is never offered when opening
 * it would immediately redirect — no bounce loop.
 *
 * No feature flag. The view shipped, so what remains are the two conditions that are properties of the
 * DEPARTMENT rather than of a rollout — and those are permanent: a medical department does not run this
 * screen, and a department with no jobs configured has nothing for a job-centric grid to show.
 *
 * Resolution order matters: everything below is derived from the chosen department, so "the department
 * hasn't resolved yet" has to be answered BEFORE the availability predicate is even evaluated.
 * `chosenDepartment.jobIds` is the tell for jobs specifically — `useJobs()` flattens "not hydrated"
 * and "none configured" into the same empty array, and only the second of those means "unavailable".
 */
export const useMonthlyViewAvailable = (): StaffingViewAvailability => {
	const { chosenDepartment, isMedicalDepartment, isInitialLoading } = useHospitalData();
	const enabledJobs = useJobs();

	// `DEFAULT_CHOSEN_DEPARTMENT.department_id` is the empty string (contexts/ChosenDepartment/consts.ts),
	// and `jobIds` is absent on it entirely, so both sentinels are "not resolved yet", never "configured
	// as empty". Callers may therefore assume a non-empty `department_id` whenever this is not "loading".
	if (!chosenDepartment.department_id || isInitialLoading || chosenDepartment.jobIds === undefined) return "loading";

	return !isMedicalDepartment && enabledJobs.length > 0 ? "available" : "unavailable";
};
